import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useAuthStore, useAppStore } from '@indipia/shared';
import {
  SiteRegistrationPopup, AssigneeBadges, useCompanyMembers, useDefaultMemberFilter, useMemberFilterState,
} from '@indipia/shared-features';
import type { AssignedMember } from '@indipia/shared-features';
import { Alert, Badge, Button, FilterChip, Modal, Page, PageScroll, Search, StepsDetail } from '@indipia/ui-core';
import { photoApi } from '../api/photoApi';
import { AssigneeFilterField } from '../components/AssigneeFilterField';
import type { UnclassifiedItem, SitePhotoItem, SiteListItem } from '../api/photoApi';
import { ThumbImg } from '../components/ThumbImg';
import { PhotoViewerLayer } from '../components/PhotoViewerLayer';
import { usePhotoNavStore } from '../store/photoNavStore';
import { siteStatusBadge } from '../utils/siteStatus';

const BASE = '/agents/photo-organizer';

const extractDong = (address: string): string => {
  const parts = address.replace(/[()]/g, '').split(/\s+/);
  const matches = parts.filter(p => /(?:동|읍|면|리)$/.test(p));
  return matches.length ? matches[matches.length - 1] : (parts[parts.length - 1] ?? address);
};

// 현장 카드 정렬 — 이름 가나다순 (Hangul 음절 블록은 코드포인트 순서=가나다 순이라 localeCompare('ko')로 충분)
const sortByName = <T extends { siteName: string }>(a: T, b: T): number => a.siteName.localeCompare(b.siteName, 'ko');

// 전체 현장 검색 — 상태 필터 칩 (SitePanel.tsx STATUS_FILTERS와 동일 값·매핑, BE `/sites` siteStatus 파라미터)
type SiteStatusFilter = 'all' | 'ACTIVE' | 'PROGRESS' | 'DONE';
const ALL_SITES_STATUS_FILTERS: { label: string; filter: SiteStatusFilter }[] = [
  { label: '전체',     filter: 'all' },
  { label: '등록',     filter: 'ACTIVE' },
  { label: '진행중',   filter: 'PROGRESS' },
  { label: '거래완료', filter: 'DONE' },
];

interface SiteGroup {
  siteId: number;
  siteName: string;
  address: string | null;
  customerName: string | null;
  siteStatus: string | null;
  siteStatusName: string | null;
  newCount: number;
  prevCount: number;
  thumbPhotos: SitePhotoItem[];
  totalOnSite: number;
  sessionPhotoIds: number[];
  // 전체 현장 목록에서 드래그로 승격된 카드만 true — 세션 배정이 전부 빠지면(newCount 0)
  // "전체 현장" 목록으로 되돌려보내기 위한 표시. GPS 자동매칭·현장등록 카드는 항상 false/undefined.
  fromPool?: boolean;
  /** 현장 담당자·관리자(2026-10-12 개선사항 D-5) — 카드 주소 아래 배지 줄 */
  chargeMembers?: AssignedMember[];
  managerMembers?: AssignedMember[];
}

// fromPool 카드가 세션 배정을 전부 잃으면(newCount 0) 매칭 목록에서 제거해 "전체 현장" 목록으로
// 되돌린다 — GPS 매칭·현장등록으로 생긴 카드는 newCount 0이어도 그대로 둔다(기존 동작 유지).
const pruneEmptyPromoted = (groups: SiteGroup[]): SiteGroup[] =>
  groups.filter(g => !(g.fromPool && g.newCount === 0));

interface ViewerPhoto {
  photoId: number;
  thumbUrl: string | null;
  originalUrl: string | null;
  takenAt: string | null;
  tags: { tagType: string; tagValue: string }[];
}

// ─── StepBar ─────────────────────────────────────────────────────
// 업로드 3화면 공용 단계 라벨 — 표준 컴포넌트 StepsDetail(D-09 §15.22)로 렌더한다.
// 업로드를 거치지 않고 바로 진입하면(fromUpload=false) 1단계를 빼고 2단계 구성으로 표시한다.
function StepBar({ uploadCount, fromUpload }: { uploadCount: number; fromUpload: boolean }) {
  const steps = fromUpload
    ? [
        { label: '사진업로드', sub: `${uploadCount}장 완료` },
        { label: '현장분류',   sub: '현장 배정' },
        { label: '태그 등록', sub: '공간·공종 태깅' },
      ]
    : [
        { label: '현장분류',   sub: '현장 배정' },
        { label: '태그 등록', sub: '공간·공종 태깅' },
      ];
  const current = fromUpload ? 1 : 0;

  return (
    <div style={{ background: 'var(--shell-bg)', padding: '0 var(--spacing-11)', flexShrink: 0 }}>
      <StepsDetail steps={steps} current={current} />
    </div>
  );
}

// ─── UncPhotoCard (미분류) — 다중 선택 + 드래그 + [x] 제거 버튼 ───────────
function UncPhotoCard({
  item, dragging, isSelected, selectedCount, onDragStart, onDragEnd, onRemove, onSelect, onViewPhoto,
}: {
  item: UnclassifiedItem;
  dragging: boolean;
  isSelected: boolean;
  selectedCount: number;
  onDragStart: () => void;
  onDragEnd: () => void;
  onRemove: () => void;
  onSelect: (e: React.MouseEvent) => void;
  onViewPhoto: () => void;
}) {
  const hasGps = item.gpsLat !== null;
  const addressFailed = hasGps && item.address == null;
  const [hovered, setHovered] = useState(false);

  return (
    <div
      draggable
      data-unc-card={item.photoId}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(item.photoId));
        if (isSelected && selectedCount > 1) {
          const ghost = document.createElement('div');
          ghost.style.cssText = 'position:fixed;top:-999px;left:0;background:var(--gt-primary);color:var(--gray-0);font-size:12px;font-weight:700;padding:6px 14px;border-radius:20px;white-space:nowrap;pointer-events:none';
          ghost.textContent = `${selectedCount}장 이동`;
          document.body.appendChild(ghost);
          e.dataTransfer.setDragImage(ghost, ghost.offsetWidth / 2, 16);
          setTimeout(() => ghost.remove(), 0);
        }
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        width: 85, height: 85,
        flexShrink: 0,
        cursor: 'grab', userSelect: 'none',
        border: isSelected ? '2px solid var(--gt-primary)' : hovered ? '1.5px solid var(--gt-primary)' : '1.5px solid var(--shell-border)',
        borderRadius: 'var(--radius-lg)',
        opacity: dragging ? 0.35 : 1,
        transition: 'border-color .15s, opacity .15s',
        boxShadow: isSelected ? '0 0 0 3px var(--gt-subtle)' : 'none',
      }}
    >
      {/* 선택됨 체크 뱃지 */}
      {isSelected && (
        <div style={{
          position: 'absolute', top: -6, left: -6,
          width: 16, height: 16, borderRadius: 'var(--radius-full)',
          background: 'var(--gt-primary)', border: '2px solid var(--gray-0)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10, pointerEvents: 'none',
        }}>
          <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="var(--gray-0)" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
        </div>
      )}

      {/* 썸네일 — 카드 전체 채움 (cursor inherit → 부모 grab 유지, 드래그 전체 영역) */}
      <div
        data-thumb-area=""
        style={{
          position: 'absolute', inset: 0,
          borderRadius: 'var(--radius-md)', overflow: 'hidden',
          background: 'var(--gt-subtle)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <ThumbImg url={item.thumbUrl} draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" fallback={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>} />
      </div>

      {/* GPS 오버레이 뱃지 */}
      <div style={{ position: 'absolute', bottom: 5, left: 0, right: 0, display: 'flex', justifyContent: 'center', zIndex: 2, pointerEvents: 'none' }}>
        {!hasGps
          ? <span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--gray-0)', background: 'var(--alpha-black-500)', padding: '2px 6px', borderRadius: 'var(--radius-sm)', whiteSpace: 'nowrap' }}>GPS없음</span>
          : addressFailed
            ? <span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--gray-0)', background: 'var(--color-error)', padding: '2px 6px', borderRadius: 'var(--radius-sm)', whiteSpace: 'nowrap' }}>주소 변환 실패</span>
            : <div style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--gray-0)', background: 'var(--gt-primary)', padding: '2px 6px', borderRadius: 'var(--radius-sm)', whiteSpace: 'nowrap' }}>
                {item.address ? extractDong(item.address) : 'GPS'}
              </div>
        }
      </div>

      {/* 호버 버튼 행 — [확대보기] + [x 제거] */}
      {hovered && (
        <>
          <button
            draggable={false}
            onClick={e => { e.stopPropagation(); onViewPhoto(); }}
            title="사진 확대보기"
            aria-label="사진 확대보기"
            style={{
              position: 'absolute', top: -7, left: -7,
              width: 24, height: 24, borderRadius: 'var(--radius-full)',
              background: 'var(--alpha-black-600)', border: '2px solid var(--gray-0)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'zoom-in', padding: 0, flexShrink: 0, zIndex: 10,
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--gray-0)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
          </button>
          <button
            draggable={false}
            onClick={e => { e.stopPropagation(); onRemove(); }}
            title="사진 제거"
            aria-label="사진 제거"
            style={{
              position: 'absolute', top: -7, right: -7,
              width: 24, height: 24, borderRadius: 'var(--radius-full)',
              background: 'var(--color-error)', border: '2px solid var(--gray-0)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', padding: 0, flexShrink: 0, zIndex: 10,
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--gray-0)" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </>
      )}
    </div>
  );
}

// ─── SiteThumb (현장 카드 썸네일) — 드래그로 다른 현장 이동 + 고무줄 다중 선택 + [-] 미분류 이동 버튼 ─────────
function SiteThumb({
  photo, onClick, onMoveToUnclassified, dragging, isSelected, selectedCount, onDragStart, onDragEnd,
}: {
  photo: SitePhotoItem;
  onClick: () => void;
  onMoveToUnclassified: () => void;
  dragging: boolean;
  isSelected: boolean;
  selectedCount: number;
  onDragStart: () => void;
  onDragEnd: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{ position: 'relative' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        draggable
        data-site-thumb={photo.photoId}
        onDragStart={e => {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', String(photo.photoId));
          if (isSelected && selectedCount > 1) {
            const ghost = document.createElement('div');
            ghost.style.cssText = 'position:fixed;top:-999px;left:0;background:var(--gt-primary);color:var(--gray-0);font-size:12px;font-weight:700;padding:6px 14px;border-radius:20px;white-space:nowrap;pointer-events:none';
            ghost.textContent = `${selectedCount}장 이동`;
            document.body.appendChild(ghost);
            e.dataTransfer.setDragImage(ghost, ghost.offsetWidth / 2, 16);
            setTimeout(() => ghost.remove(), 0);
          }
          onDragStart();
        }}
        onDragEnd={onDragEnd}
        onClick={e => { e.stopPropagation(); onClick(); }}
        style={{
          width: 64, height: 64, borderRadius: 'var(--radius-md)', overflow: 'hidden',
          background: 'var(--gt-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, cursor: 'grab',
          border: isSelected ? '2px solid var(--gt-primary)' : 'none',
          boxShadow: isSelected ? '0 0 0 2px var(--gt-subtle)' : 'none',
          opacity: dragging ? 0.35 : hovered ? 0.8 : 1, transition: 'opacity .15s, border-color .15s',
        }}
      >
        <ThumbImg url={photo.thumbUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" fallback={<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>} />
      </div>
      {/* 선택됨 체크 뱃지 */}
      {isSelected && (
        <div style={{
          position: 'absolute', top: -6, left: -6,
          width: 16, height: 16, borderRadius: 'var(--radius-full)',
          background: 'var(--gt-primary)', border: '2px solid var(--gray-0)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10, pointerEvents: 'none',
        }}>
          <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="var(--gray-0)" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
        </div>
      )}
      {/* [-] 미분류 이동 버튼 — 호버 시만 표시 */}
      {hovered && (
        <button
          onClick={e => { e.stopPropagation(); onMoveToUnclassified(); }}
          title="미분류 사진 영역으로 이동"
          aria-label="미분류 사진 영역으로 이동"
          style={{
            position: 'absolute', top: -7, right: -7,
            width: 24, height: 24, borderRadius: 'var(--radius-full)',
            background: 'var(--shell-hint)', border: '2px solid var(--gray-0)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', padding: 0, flexShrink: 0,
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--gray-0)" strokeWidth="3.5" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12" /></svg>
        </button>
      )}
    </div>
  );
}

// ─── ClassifySiteMain ─────────────────────────────────────────────
export function ClassifySiteMain() {
  const navigate = useNavigate();
  const classifyResult = usePhotoNavStore(s => s.classifyResult);
  const fromUpload = usePhotoNavStore(s => s.fromUploadFlow);
  const currentSessionId = usePhotoNavStore(s => s.currentSessionId);
  const batchPhotos = usePhotoNavStore(s => s.batchPhotos);
  const setTagEditGroups = usePhotoNavStore(s => s.setTagEditGroups);
  const setClassifySiteGroups = usePhotoNavStore(s => s.setClassifySiteGroups);
  const setTagEditReturnPath = usePhotoNavStore(s => s.setTagEditReturnPath);

  const [siteGroups, setSiteGroups] = useState<SiteGroup[]>([]);
  const [unclassifiedItems, setUnclassifiedItems] = useState<UnclassifiedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [draggingPhotoId, setDraggingPhotoId] = useState<number | null>(null);
  const [draggingSiteThumb, setDraggingSiteThumb] = useState<{ photoId: number; siteId: number } | null>(null);
  const [selectedSitePhotoIds, setSelectedSitePhotoIds] = useState<Set<number>>(new Set());
  const [dropTargetId, setDropTargetId] = useState<number | null>(null);
  const [showSiteRegPopup, setShowSiteRegPopup] = useState(false);
  const [showInfoBanner, setShowInfoBanner] = useState(true);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<Set<number>>(new Set());
  const [lastClickedId, setLastClickedId] = useState<number | null>(null);
  const [viewer, setViewer] = useState<{ photos: ViewerPhoto[]; initialIndex: number } | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [uncOffset, setUncOffset] = useState(0);
  const [uncHasMore, setUncHasMore] = useState(false);
  const [uncLoadingMore, setUncLoadingMore] = useState(false);
  const [uncTotalCount, setUncTotalCount] = useState(0);
  const uncScrollRef = useRef<HTMLDivElement>(null);
  const uncSentinelRef = useRef<HTMLDivElement>(null);

  // 전체 현장 목록에서 방금 배정받아 승격된 현장 — 가나다순 정렬로 위치는 이동해도 배지로 잠깐 표시
  const [justPromotedSiteId, setJustPromotedSiteId] = useState<number | null>(null);
  const justPromotedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const markJustPromoted = (siteId: number) => {
    setJustPromotedSiteId(siteId);
    if (justPromotedTimerRef.current) clearTimeout(justPromotedTimerRef.current);
    justPromotedTimerRef.current = setTimeout(() => setJustPromotedSiteId(null), 2500);
  };

  // 전체 현장 불러오기 — 헤더에 고정된 토글·검색, 결과는 현장 카드 목록 하단에 이어붙임
  const [allSites, setAllSites] = useState<SiteListItem[]>([]);
  const [allSitesOffset, setAllSitesOffset] = useState(0);
  const [allSitesHasMore, setAllSitesHasMore] = useState(false);
  const [allSitesLoading, setAllSitesLoading] = useState(false);
  const [allSitesStarted, setAllSitesStarted] = useState(false);
  const [allSitesVisible, setAllSitesVisible] = useState(false);
  const [allSitesKeyword, setAllSitesKeyword] = useState('');
  const [allSitesStatusFilter, setAllSitesStatusFilter] = useState<SiteStatusFilter>('all');
  const allSitesSentinelRef = useRef<HTMLDivElement>(null);
  const allSitesDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const allSitesAbortRef = useRef<AbortController | null>(null);

  // 담당자·관리자 조건(D-5) — [전체 현장 불러오기]로 추가되는 현장 카드에만 적용한다.
  // 이번 업로드에서 GPS로 자동 매칭된 현장은 조건과 무관하게 항상 표시(사진 배정 누락 방지)하고 배지만 단다.
  // 기본값은 공용 훅 — 로그인 정보가 늦게 들어와도 직원=[나]/사장님=[] 로 맞춰진다
  const { myId: assignMyId } = useDefaultMemberFilter();
  const { data: companyMembers = [] } = useCompanyMembers();
  const [allSitesMemberIds, setAllSitesMemberIds] = useMemberFilterState();
  // refreshAllSites 는 의존성 없는 콜백이라 최신 조건을 ref 로 읽는다
  const allSitesMemberIdsRef = useRef<number[]>(allSitesMemberIds);
  allSitesMemberIdsRef.current = allSitesMemberIds;

  // 언마운트 시 대기 중인 타이머·요청 정리 — 늦게 도착한 응답/콜백이 사라진 컴포넌트를 건드리지 않도록
  useEffect(() => {
    return () => {
      if (justPromotedTimerRef.current) clearTimeout(justPromotedTimerRef.current);
      if (allSitesDebounceRef.current) clearTimeout(allSitesDebounceRef.current);
      allSitesAbortRef.current?.abort();
    };
  }, []);

  // 고무줄 선택 — mode로 미분류 패널(unc) / 현장별 사진 썸네일(site) 대상 전환
  const isDeletingRef = useRef(false);
  const rbStateRef = useRef({ active: false, startX: 0, startY: 0, shiftHeld: false, ctrlHeld: false, mode: 'unc' as 'unc' | 'site' });
  const rbBoxRef = useRef<HTMLDivElement | null>(null);
  const rbInitialSelRef = useRef<Set<number>>(new Set()); // Shift+드래그 시 기존 선택 스냅샷

  useEffect(() => {
    const box = document.createElement('div');
    box.style.cssText = 'position:fixed;border:1.5px solid var(--gt-primary);background:color-mix(in srgb, var(--gt-primary) 10%, transparent);border-radius:var(--radius-sm);pointer-events:none;display:none;z-index:10';
    document.body.appendChild(box);
    rbBoxRef.current = box;
    return () => { box.remove(); };
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!rbStateRef.current.active) return;
      const box = rbBoxRef.current;
      if (!box) return;
      const { startX, startY } = rbStateRef.current;
      const x = Math.min(startX, e.clientX);
      const y = Math.min(startY, e.clientY);
      const w = Math.abs(e.clientX - startX);
      const h = Math.abs(e.clientY - startY);
      box.style.left = x + 'px';
      box.style.top = y + 'px';
      box.style.width = w + 'px';
      box.style.height = h + 'px';
      // 교차하는 카드 선택 — mode에 따라 대상 셀렉터/상태가 달라짐
      const isSiteMode = rbStateRef.current.mode === 'site';
      const attr = isSiteMode ? 'data-site-thumb' : 'data-unc-card';
      const cards = document.querySelectorAll<HTMLElement>(`[${attr}]`);
      const next = new Set<number>();
      cards.forEach(card => {
        const r = card.getBoundingClientRect();
        if (!(r.right < x || r.left > x + w || r.bottom < y || r.top > y + h)) {
          const id = parseInt(card.getAttribute(attr) ?? '');
          if (!isNaN(id)) next.add(id);
        }
      });
      const merged = (rbStateRef.current.shiftHeld || rbStateRef.current.ctrlHeld)
        ? new Set<number>([...rbInitialSelRef.current, ...next])
        : next;
      (isSiteMode ? setSelectedSitePhotoIds : setSelectedPhotoIds)(merged);
    };
    const handleMouseUp = () => {
      if (!rbStateRef.current.active) return;
      rbStateRef.current.active = false;
      if (rbBoxRef.current) rbBoxRef.current.style.display = 'none';
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const loadMoreUnclassified = useCallback(async () => {
    if (uncLoadingMore || !uncHasMore) return;
    setUncLoadingMore(true);
    try {
      const res = await photoApi.getUnclassifiedPhotos(20, uncOffset);
      const data = res.data.data;
      setUnclassifiedItems(prev => {
        const existingIds = new Set(prev.map(p => p.photoId));
        return [...prev, ...data.items.filter(p => !existingIds.has(p.photoId))];
      });
      setUncTotalCount(data.totalCount);
      const newOffset = uncOffset + data.items.length;
      setUncOffset(newOffset);
      setUncHasMore(newOffset < data.totalCount);
    } catch {
      // silent
    } finally {
      setUncLoadingMore(false);
    }
  }, [uncLoadingMore, uncHasMore, uncOffset]);

  // 재매칭 후 미분류 목록을 처음부터 다시 읽어 갱신한다(매칭된 장은 목록에서 빠진다).
  const refreshUnclassified = useCallback(async () => {
    try {
      const res = await photoApi.getUnclassifiedPhotos(20, 0);
      const data = res.data.data;
      setUnclassifiedItems(data.items);
      setUncTotalCount(data.totalCount);
      setUncOffset(data.items.length);
      setUncHasMore(data.items.length < data.totalCount);
    } catch { /* silent */ }
  }, []);

  const [rematching, setRematching] = useState(false);
  const hasServiceError = unclassifiedItems.some(p => p.matchReason === 'SERVICE_ERROR');
  const handleRematch = useCallback(async () => {
    if (rematching) return;
    setRematching(true);
    try {
      const { data } = await photoApi.rematch();
      const r = data.data;
      await refreshUnclassified();
      if (r.matched > 0) toast.success(`${r.matched}장이 현장에 배정됐어요`);
      else if (r.stillError > 0) toast.error('매칭 서비스가 아직 불안정해요. 잠시 후 다시 시도해 주세요');
      else toast('현장에 배정할 사진이 없어요');
    } catch {
      toast.error('재매칭에 실패했습니다');
    } finally {
      setRematching(false);
    }
  }, [rematching, refreshUnclassified]);

  useEffect(() => {
    const sentinel = uncSentinelRef.current;
    if (!sentinel || !uncHasMore) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) void loadMoreUnclassified(); },
      { threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [uncHasMore, loadMoreUnclassified]);

  // 전체 현장 목록 — 스크롤 시 offset 늘려가며 추가 로딩 (미분류 패널과 동일한 페이지네이션 패턴).
  // refreshAllSites와 in-flight 요청을 공유해서, 검색어 변경 중 스크롤로 겹쳐 나가는 요청을 막는다
  // (allSitesLoading은 loadMore끼리의 중복만 막고 refresh와는 경합할 수 있어 AbortController로 이전 요청 자체를 취소).
  const loadMoreAllSites = useCallback(async () => {
    if (allSitesLoading || !allSitesHasMore) return;
    allSitesAbortRef.current?.abort();
    const controller = new AbortController();
    allSitesAbortRef.current = controller;
    setAllSitesLoading(true);
    try {
      const res = await photoApi.getSiteList({ limit: 20, offset: allSitesOffset, keyword: allSitesKeyword || undefined, siteStatus: allSitesStatusFilter === 'all' ? undefined : allSitesStatusFilter, sort: 'siteName', memberIds: allSitesMemberIdsRef.current }, controller.signal);
      if (controller.signal.aborted) return;
      const data = res.data.data;
      // BE(/sites)가 전체 집합을 이름순으로 정렬한 뒤 페이지 슬라이스해서 내려주므로 FE에서 재정렬할 필요 없음
      setAllSites(prev => {
        const existingIds = new Set(prev.map(s => s.siteId));
        return [...prev, ...data.sites.filter(s => !existingIds.has(s.siteId))];
      });
      const newOffset = allSitesOffset + data.sites.length;
      setAllSitesOffset(newOffset);
      setAllSitesHasMore(newOffset < data.totalSiteCount);
    } catch {
      if (controller.signal.aborted) return;
      toast.error('현장 목록을 불러오는데 실패했습니다');
    } finally {
      if (!controller.signal.aborted) setAllSitesLoading(false);
    }
  }, [allSitesLoading, allSitesHasMore, allSitesOffset, allSitesKeyword, allSitesStatusFilter]);

  // 검색어·상태필터가 바뀌었을 때 처음부터 다시 읽는다 — 최신값을 인자로 명시적으로 받아 쓴다(state 지연 방지).
  // 이전에 나가 있던 요청(loadMore든 refresh든)은 무조건 취소해서 늦게 도착한 응답이 최신 상태를 덮어쓰지 않게 한다.
  const refreshAllSites = useCallback(async (keyword: string, statusFilter: SiteStatusFilter) => {
    allSitesAbortRef.current?.abort();
    const controller = new AbortController();
    allSitesAbortRef.current = controller;
    setAllSitesLoading(true);
    try {
      const res = await photoApi.getSiteList({ limit: 20, offset: 0, keyword: keyword || undefined, siteStatus: statusFilter === 'all' ? undefined : statusFilter, sort: 'siteName', memberIds: allSitesMemberIdsRef.current }, controller.signal);
      if (controller.signal.aborted) return;
      const data = res.data.data;
      setAllSites(data.sites);
      setAllSitesOffset(data.sites.length);
      setAllSitesHasMore(data.sites.length < data.totalSiteCount);
    } catch {
      if (controller.signal.aborted) return;
      toast.error('현장 목록을 불러오는데 실패했습니다');
    } finally {
      if (!controller.signal.aborted) setAllSitesLoading(false);
    }
  }, []);

  // site-list "미분류 사진" 카드에서 넘어온 경우 — 전체 현장 목록을 진입과 동시에 펼쳐서 보여준다.
  // 신호를 컴포넌트 인스턴스당 한 번(lazy useState 초기화)만 스냅샷으로 떠서 로컬 상태로 들고 있는다 —
  // StrictMode(dev)가 mount→cleanup→mount를 한 번 더 시뮬레이션할 때, effect 안에서 store 플래그를
  // 직접 읽고 지우면 첫(가짜) mount가 플래그를 먼저 소비해버려 진짜 mount에서는 아무 일도 안 일어난다.
  // fetch 자체는 이미 AbortController로 취소 가능하므로(useCallback 참고), 가짜 mount의 요청은
  // cleanup에서 취소되고 진짜 mount가 다시 트리거하면 정상 동작한다.
  const [showAllSitesOnEntry] = useState(() => usePhotoNavStore.getState().classifyShowAllSitesOnEntry);
  useEffect(() => {
    usePhotoNavStore.getState().setClassifyShowAllSitesOnEntry(false);
    if (!showAllSitesOnEntry) return;
    setAllSitesStarted(true);
    setAllSitesVisible(true);
    void refreshAllSites('', 'all');
  }, [showAllSitesOnEntry, refreshAllSites]);

  useEffect(() => {
    const sentinel = allSitesSentinelRef.current;
    if (!sentinel || !allSitesVisible || !allSitesHasMore) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) void loadMoreAllSites(); },
      { threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [allSitesVisible, allSitesHasMore, loadMoreAllSites]);

  // 토글 버튼 — 처음 클릭 시에만 fetch, 이후엔 이미 불러온 카드를 보이기/숨기기만 한다
  const handleToggleAllSites = () => {
    if (allSitesStarted && allSitesVisible) { setAllSitesVisible(false); return; }
    setAllSitesVisible(true);
    if (!allSitesStarted) {
      setAllSitesStarted(true);
      void refreshAllSites(allSitesKeyword, allSitesStatusFilter);
    }
  };

  // 검색창은 헤더에 항상 고정 노출 — 타이핑하면 아직 시작 전이어도 바로 불러오기 시작
  const handleAllSitesKeywordChange = (value: string) => {
    setAllSitesKeyword(value);
    if (!allSitesStarted) {
      setAllSitesStarted(true);
      setAllSitesVisible(true);
      void refreshAllSites(value, allSitesStatusFilter);
      return;
    }
    if (!allSitesVisible) setAllSitesVisible(true);
    if (allSitesDebounceRef.current) clearTimeout(allSitesDebounceRef.current);
    allSitesDebounceRef.current = setTimeout(() => { void refreshAllSites(value, allSitesStatusFilter); }, 300);
  };

  // 담당자·관리자 조건 변경 — 이미 불러온 전체 현장 목록만 다시 읽는다(GPS 매칭 카드는 그대로)
  const handleAllSitesMembersChange = (ids: number[]) => {
    setAllSitesMemberIds(ids);
    allSitesMemberIdsRef.current = ids;
    if (!allSitesStarted) return;
    if (allSitesDebounceRef.current) clearTimeout(allSitesDebounceRef.current);
    void refreshAllSites(allSitesKeyword, allSitesStatusFilter);
  };

  // 상태 칩은 클릭 즉시 반영(타이핑이 아니므로 디바운스 불필요)
  const handleAllSitesStatusChange = (filter: SiteStatusFilter) => {
    setAllSitesStatusFilter(filter);
    if (!allSitesStarted) {
      setAllSitesStarted(true);
      setAllSitesVisible(true);
    } else if (!allSitesVisible) {
      setAllSitesVisible(true);
    }
    if (allSitesDebounceRef.current) clearTimeout(allSitesDebounceRef.current);
    void refreshAllSites(allSitesKeyword, filter);
  };

  // 이미 매칭 목록(siteGroups)에 떠 있는 현장은 전체 현장 목록에서 제외 — 배정되는 순간
  // siteGroups로 승격되면서 이 필터로 자동으로 빠진다(별도 제거 로직 불필요).
  // allSitesVisible이 false(숨기기 상태)면 빈 배열 — 버튼으로 숨겼을 때 카드도 같이 사라지도록.
  const visibleAllSites = allSitesVisible
    ? allSites.filter(s => !siteGroups.some(g => g.siteId === s.siteId))
    : [];
  // 전체 현장을 불러왔지만 담당자·관리자/상태 조건으로 서버 결과 자체가 0건
  const allSitesFilteredEmpty = allSitesVisible && allSitesStarted && !allSitesLoading && allSites.length === 0
    && (allSitesMemberIds.length > 0 || allSitesStatusFilter !== 'all');
  const allSitesAsGroups: SiteGroup[] = visibleAllSites.map(s => ({
    siteId: s.siteId,
    siteName: s.siteName,
    address: s.address,
    customerName: s.customerName,
    siteStatus: s.siteStatus,
    siteStatusName: s.siteStatusName,
    newCount: 0,
    prevCount: s.photoCount,
    thumbPhotos: [],
    totalOnSite: s.photoCount,
    sessionPhotoIds: [],
    chargeMembers: s.chargeMembers ?? [],
    managerMembers: s.managerMembers ?? [],
  }));

  const newUnclassified = currentSessionId != null
    ? unclassifiedItems.filter(p => p.sessionId === currentSessionId)
    : unclassifiedItems.filter(p =>
        (classifyResult?.results.filter(r => r.matchedSiteId === null).map(r => r.photoId) ?? []).includes(p.photoId)
      );
  const newUnclassifiedIds = new Set(newUnclassified.map(p => p.photoId));
  const oldUnclassified = unclassifiedItems.filter(p => !newUnclassifiedIds.has(p.photoId));

  const newUploadTotalCount = newUnclassified.length;
  const oldUnclassifiedTotalCount = Math.max(0, uncTotalCount - newUploadTotalCount);

  useEffect(() => {
    const user = useAuthStore.getState().user;
    if (!user) return;

    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      try {
        const uncRes = await photoApi.getUnclassifiedPhotos(20, 0, controller.signal);
        if (controller.signal.aborted) return;
        const uncData = uncRes.data.data;
        setUnclassifiedItems(uncData.items);
        setUncTotalCount(uncData.totalCount);
        setUncOffset(uncData.items.length);
        setUncHasMore(uncData.items.length < uncData.totalCount);

        // 태그 화면 이전 버튼으로 돌아온 경우 — 저장된 스냅샷 복원
        const snapshot = usePhotoNavStore.getState().classifySiteGroups;
        if (snapshot) {
          usePhotoNavStore.getState().clearClassifySiteGroups();
          setSiteGroups([...snapshot].sort(sortByName));
          return;
        }

        if (classifyResult) {
          const siteMap = new Map<number, { siteName: string; address: string | null; newCount: number }>();
          for (const r of classifyResult.results) {
            if (r.matchedSiteId === null) continue;
            const existing = siteMap.get(r.matchedSiteId);
            if (existing) {
              existing.newCount++;
            } else {
              siteMap.set(r.matchedSiteId, {
                siteName: r.siteName ?? `현장 #${r.matchedSiteId}`,
                address: r.address ?? null,
                newCount: 1,
              });
            }
          }

          const sessionIdsBySite = new Map<number, number[]>();
          for (const r of classifyResult.results) {
            if (r.matchedSiteId === null) continue;
            if (!sessionIdsBySite.has(r.matchedSiteId)) sessionIdsBySite.set(r.matchedSiteId, []);
            sessionIdsBySite.get(r.matchedSiteId)!.push(r.photoId);
          }

          const batchPhotoMap = new Map(batchPhotos.map(p => [p.photoId, p]));
          const classifyAddressMap = new Map(classifyResult.results.map(r => [r.photoId, r.address ?? null]));
          const distanceMap = new Map(classifyResult.results.map(r => [r.photoId, r.distanceM]));

          const groups: SiteGroup[] = await Promise.all(
            Array.from(siteMap.entries()).map(async ([siteId, info]) => {
              const sessionIds = sessionIdsBySite.get(siteId) ?? [];
              // 세션 사진 전체 thumbUrl 보존 (태그 화면에서 이미지 복원에 사용)
              const thumbPhotos: SitePhotoItem[] = sessionIds
                .filter(id => batchPhotoMap.has(id))
                .map(id => {
                  const p = batchPhotoMap.get(id)!;
                  const addr = classifyAddressMap.get(p.photoId) ?? null;
                  const dist = distanceMap.get(p.photoId);
                  const hasGps = addr !== null || (dist !== undefined && dist !== null);
                  return { photoId: p.photoId, thumbUrl: p.thumbUrl, takenAt: p.takenAt, registeredAt: p.registeredAt ?? null, gpsLat: hasGps ? 1 : null, address: addr };
                });
              let totalOnSite = info.newCount;
              let customerName: string | null = null;
              let siteStatus: string | null = null;
              let siteStatusName: string | null = null;
              let chargeMembers: AssignedMember[] = [];
              let managerMembers: AssignedMember[] = [];
              try {
                const detailRes = await photoApi.getSiteDetail(siteId);
                totalOnSite = detailRes.data.data.photoCount;
                customerName = detailRes.data.data.customerName;
                siteStatus = detailRes.data.data.siteStatus;
                siteStatusName = detailRes.data.data.siteStatusName;
                chargeMembers = detailRes.data.data.chargeMembers ?? [];
                managerMembers = detailRes.data.data.managerMembers ?? [];
              } catch { /* prevCount·고객명·상태 조회 실패 무시 */ }
              return {
                siteId,
                siteName: info.siteName,
                address: info.address,
                customerName,
                siteStatus,
                siteStatusName,
                chargeMembers,
                managerMembers,
                newCount: info.newCount,
                prevCount: Math.max(0, totalOnSite - info.newCount),
                thumbPhotos,
                totalOnSite,
                sessionPhotoIds: sessionIds,
              };
            })
          );
          if (controller.signal.aborted) return;
          setSiteGroups(groups.sort(sortByName));
        }
      } catch {
        if (controller.signal.aborted) return;
        toast.error('분류 결과를 불러오는데 실패했습니다');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [classifyResult]);

  // 카드 클릭 선택 (단순/Ctrl/Shift)
  const handleCardClick = (photoId: number, e: React.MouseEvent) => {
    if (e.shiftKey && lastClickedId !== null) {
      const allItems = [...newUnclassified, ...oldUnclassified];
      const allIds = allItems.map(p => p.photoId);
      const a = allIds.indexOf(lastClickedId), b = allIds.indexOf(photoId);
      if (a !== -1 && b !== -1) {
        const lo = Math.min(a, b), hi = Math.max(a, b);
        setSelectedPhotoIds(prev => {
          const next = new Set(prev);
          allIds.slice(lo, hi + 1).forEach(id => next.add(id));
          return next;
        });
      }
    } else {
      // Ctrl/Cmd 없이도 클릭할 때마다 토글 — 여러 장을 순서대로 클릭해서 누적 선택 가능
      setSelectedPhotoIds(prev => {
        const next = new Set(prev);
        if (next.has(photoId)) next.delete(photoId); else next.add(photoId);
        return next;
      });
      setLastClickedId(photoId);
    }
  };

  // 고무줄 선택 시작 (빈 영역 mousedown)
  const handleContainerMouseDown = (e: React.MouseEvent) => {
    if ((e.target as Element).closest('[data-unc-card]')) return;
    if (e.button !== 0) return;
    const additive = e.shiftKey || e.ctrlKey || e.metaKey;
    rbStateRef.current = { active: true, startX: e.clientX, startY: e.clientY, shiftHeld: e.shiftKey, ctrlHeld: e.ctrlKey || e.metaKey, mode: 'unc' };
    rbInitialSelRef.current = additive ? new Set(selectedPhotoIds) : new Set();
    const box = rbBoxRef.current;
    if (box) {
      box.style.left = e.clientX + 'px';
      box.style.top = e.clientY + 'px';
      box.style.width = '0';
      box.style.height = '0';
      box.style.display = 'block';
    }
    if (!additive) setSelectedPhotoIds(new Set());
    e.preventDefault();
  };

  // 현장별 사진 썸네일 영역 고무줄 선택 시작 (빈 영역 mousedown)
  const handleSiteContainerMouseDown = (e: React.MouseEvent) => {
    if ((e.target as Element).closest('[data-site-thumb]')) return;
    if (e.button !== 0) return;
    const additive = e.shiftKey || e.ctrlKey || e.metaKey;
    rbStateRef.current = { active: true, startX: e.clientX, startY: e.clientY, shiftHeld: e.shiftKey, ctrlHeld: e.ctrlKey || e.metaKey, mode: 'site' };
    rbInitialSelRef.current = additive ? new Set(selectedSitePhotoIds) : new Set();
    const box = rbBoxRef.current;
    if (box) {
      box.style.left = e.clientX + 'px';
      box.style.top = e.clientY + 'px';
      box.style.width = '0';
      box.style.height = '0';
      box.style.display = 'block';
    }
    if (!additive) setSelectedSitePhotoIds(new Set());
    e.preventDefault();
  };

  // 드래그 중인 카드가 선택 상태라면 선택된 전체가 "드래그 중"으로 표시
  const isDragging = (photoId: number): boolean => {
    if (draggingPhotoId === null) return false;
    if (draggingPhotoId === photoId) return true;
    return selectedPhotoIds.has(draggingPhotoId) && selectedPhotoIds.has(photoId);
  };

  // 현장별 사진 썸네일 버전 — 드래그 중인 썸네일이 선택 상태라면 선택된 전체를 "드래그 중"으로 표시
  const isSiteThumbDragging = (photoId: number): boolean => {
    if (!draggingSiteThumb) return false;
    if (draggingSiteThumb.photoId === photoId) return true;
    return selectedSitePhotoIds.has(draggingSiteThumb.photoId) && selectedSitePhotoIds.has(photoId);
  };

  // 미분류 사진 제거 [x] — 확인 모달 표시
  const handleRemoveUnclassified = (photoId: number) => {
    setDeleteConfirmId(photoId);
  };

  const confirmDelete = async () => {
    if (deleteConfirmId === null || isDeletingRef.current) return;
    isDeletingRef.current = true;
    const photoId = deleteConfirmId;
    setDeleteConfirmId(null);
    const user = useAuthStore.getState().user;
    if (user) {
      try {
        await photoApi.deletePhoto(photoId);
      } catch {
        toast.error('사진 삭제에 실패했습니다');
        isDeletingRef.current = false;
        return;
      }
    }
    setUnclassifiedItems(prev => prev.filter(p => p.photoId !== photoId));
    setSelectedPhotoIds(prev => { const next = new Set(prev); next.delete(photoId); return next; });
    setUncTotalCount(prev => Math.max(0, prev - 1));
    isDeletingRef.current = false;
  };

  // 현장 썸네일 → 미분류 영역으로 이동 [-]
  const handleMoveToUnclassified = async (siteId: number, photo: SitePhotoItem) => {
    const prevUnclassified = unclassifiedItems;
    const prevSiteGroups = siteGroups;

    setSiteGroups(prev => pruneEmptyPromoted(prev.map(g =>
      g.siteId === siteId
        ? {
            ...g,
            thumbPhotos: g.thumbPhotos.filter(p => p.photoId !== photo.photoId),
            newCount: Math.max(0, g.newCount - 1),
            totalOnSite: Math.max(0, g.totalOnSite - 1),
            sessionPhotoIds: g.sessionPhotoIds.filter(id => id !== photo.photoId),
          }
        : g
    )));
    setUnclassifiedItems(prev => [{
      photoId: photo.photoId,
      thumbUrl: photo.thumbUrl,
      takenDtm: photo.takenAt,
      gpsLat: photo.gpsLat ?? null,
      // SitePhotoItem엔 gpsLng가 없어 원래 값을 알 수 없다 — 이 화면에서 안 쓰이는
      // 필드라(hasGps 판정은 gpsLat만 봄) null로 둔다.
      gpsLng: null,
      sessionId: photo.sessionId ?? currentSessionId,
      address: photo.address ?? null,
    }, ...prev]);
    setUncTotalCount(prev => prev + 1);
    setSelectedSitePhotoIds(prev => { const next = new Set(prev); next.delete(photo.photoId); return next; });

    const user = useAuthStore.getState().user;
    if (user) {
      try {
        const { data } = await photoApi.assignSite([{ photoId: photo.photoId, siteId: null }]);
        // BE 가 미분류 복귀 시 lazy 역지오코딩한 주소를 돌려준다 — 낙관적 업데이트로 넣어둔 null 을 실제 주소로 교체
        const addr = data.data.addresses[String(photo.photoId)];
        if (addr) {
          setUnclassifiedItems(prev => prev.map(p =>
            p.photoId === photo.photoId ? { ...p, address: addr, gpsLat: 1 } : p
          ));
        }
      } catch {
        // 이미 삭제된 경우 현장 카드 복원 금지 — 함수형 업데이터로 최신 상태 확인
        setUnclassifiedItems(current => {
          if (!current.some(p => p.photoId === photo.photoId)) return current;
          setSiteGroups(prevSiteGroups);
          setUncTotalCount(prev => Math.max(0, prev - 1));
          return prevUnclassified;
        });
        toast.error('미분류 이동에 실패했습니다');
      }
    }
  };

  const handleSiteRegComplete = async (siteId: number) => {
    setShowSiteRegPopup(false);
    try {
      // 등록 응답의 siteId로 단건 조회 (P-F1) — 이전엔 전체목록(searchCustomerSites('') limit30)에서
      // find로 걸러 정렬(created)상 신규 현장이 첫 페이지 밖으로 밀리면 못 찾는 버그가 있었다.
      const res = await photoApi.getSiteDetail(siteId);
      const found = res.data.data;

      // 신규 현장 반경 내 미분류 사진 흡수(Photo 현장등록 미분류흡수 설계 v1.2) — 실패해도
      // 현장 등록 자체는 이미 완료된 상태이므로 카드는 정상 추가하고 흡수만 건너뛴다.
      let totalOnSite = 0;
      let thumbPhotos: SitePhotoItem[] = [];
      let absorbedIds: number[] = [];
      try {
        const absorbRes = await photoApi.absorbPhotos(siteId);
        absorbedIds = absorbRes.data.data.absorbedPhotoIds;
        const absorbedCount = absorbRes.data.data.count;
        if (absorbedCount > 0) {
          const siteRes = await photoApi.getSitePhotos(siteId, 4);
          totalOnSite = siteRes.data.data.totalCount;
          thumbPhotos = siteRes.data.data.photos;
          await refreshUnclassified();
          toast.success(`미분류 사진 ${absorbedCount}장이 이 현장으로 이동했어요`);
        }
      } catch {
        // 흡수 실패는 비치명적 — 미분류는 그대로 두고 사용자가 수동 배정하게 둔다
      }

      setSiteGroups(prev => {
        const idx = prev.findIndex(g => g.siteId === siteId);
        if (idx === -1) {
          // 최상단에 배정한 뒤 가나다순으로 재정렬 — 결과 위치는 이름순이 되지만 삽입 자체는 최상단 취급
          return [{
            siteId,
            siteName: found?.siteName ?? '신규 현장',
            address: found?.address ?? null,
            customerName: found?.customerName ?? null,
            siteStatus: found?.siteStatus ?? null,
            siteStatusName: found?.siteStatusName ?? null,
            chargeMembers: found?.chargeMembers ?? [],
            managerMembers: found?.managerMembers ?? [],
            // sessionPhotoIds: 흡수분도 "이번 세션에 이 현장으로 배정된 사진"이라 다음(태그 등록) 화면
            // 진입 시 필터(sessionPhotoIds.length > 0)에 걸려야 한다 — 비워두면 신규 현장이 그대로 누락됨.
            newCount: 0, prevCount: totalOnSite, thumbPhotos, totalOnSite, sessionPhotoIds: absorbedIds,
          }, ...prev].sort(sortByName);
        }
        // 이미 카드가 떠 있는 현장(기존 현장 재선택 등)이어도, 이번에 새로 흡수된 사진이
        // 있으면 그 결과(썸네일·개수·sessionPhotoIds)는 카드에 반드시 병합해야 한다 —
        // 그냥 스킵하면 흡수 토스트는 뜨는데 카드엔 반영이 안 되는 불일치가 생긴다.
        if (absorbedIds.length === 0) return prev;
        const next = [...prev];
        const g = next[idx];
        const existingIds = new Set(g.thumbPhotos.map(p => p.photoId));
        next[idx] = {
          ...g,
          totalOnSite: totalOnSite || g.totalOnSite,
          prevCount: totalOnSite || g.prevCount,
          thumbPhotos: [...thumbPhotos.filter(p => !existingIds.has(p.photoId)), ...g.thumbPhotos],
          sessionPhotoIds: [...absorbedIds, ...g.sessionPhotoIds],
        };
        return next;
      });
    } catch {
      toast.error('현장 정보를 불러오는데 실패했습니다');
    }
  };

  // 드래그앤드롭 → UI 즉시 반영 + API 즉시 호출. 대상이 아직 siteGroups에 없는 "전체 현장" 카드라면
  // 이번 배정을 계기로 상단 매칭 목록에 새로 승격시킨다(전체 현장 목록에선 자동으로 제외됨).
  const handleDrop = async (siteId: number) => {
    if (draggingPhotoId === null) return;

    const idsToAssign = (selectedPhotoIds.has(draggingPhotoId) && selectedPhotoIds.size > 0)
      ? Array.from(selectedPhotoIds)
      : [draggingPhotoId];

    const itemsToMove = unclassifiedItems.filter(p => idsToAssign.includes(p.photoId));
    const prevUnclassified = unclassifiedItems;
    const prevSiteGroups = siteGroups;
    const poolSite = allSites.find(s => s.siteId === siteId);

    setUnclassifiedItems(prev => prev.filter(p => !idsToAssign.includes(p.photoId)));
    setSiteGroups(prev => {
      const newThumbs: SitePhotoItem[] = itemsToMove.map(p => ({
        photoId: p.photoId,
        thumbUrl: p.thumbUrl,
        takenAt: p.takenDtm,
        registeredAt: p.createdAt ?? null,
        gpsLat: p.gpsLat,
        address: p.address ?? null,
        sessionId: p.sessionId ?? null,
      }));
      if (!prev.some(g => g.siteId === siteId)) {
        markJustPromoted(siteId);
        return [{
          siteId,
          siteName: poolSite?.siteName ?? `현장 #${siteId}`,
          address: poolSite?.address ?? null,
          customerName: poolSite?.customerName ?? null,
          siteStatus: poolSite?.siteStatus ?? null,
          siteStatusName: poolSite?.siteStatusName ?? null,
          chargeMembers: poolSite?.chargeMembers ?? [],
          managerMembers: poolSite?.managerMembers ?? [],
          newCount: idsToAssign.length,
          prevCount: poolSite?.photoCount ?? 0,
          thumbPhotos: newThumbs,
          totalOnSite: (poolSite?.photoCount ?? 0) + idsToAssign.length,
          sessionPhotoIds: idsToAssign,
          fromPool: true,
        }, ...prev].sort(sortByName);
      }
      return prev.map(g => {
        if (g.siteId !== siteId) return g;
        return {
          ...g,
          newCount: g.newCount + idsToAssign.length,
          totalOnSite: g.totalOnSite + idsToAssign.length,
          thumbPhotos: [...newThumbs, ...g.thumbPhotos],
          sessionPhotoIds: [...idsToAssign, ...g.sessionPhotoIds],
        };
      });
    });
    setSelectedPhotoIds(new Set());
    setDraggingPhotoId(null);
    setDropTargetId(null);
    setUncTotalCount(prev => Math.max(0, prev - idsToAssign.length));

    const user = useAuthStore.getState().user;
    if (user) {
      try {
        await photoApi.assignSite(
          idsToAssign.map(photoId => ({ photoId, siteId })),
        );
      } catch {
        setUnclassifiedItems(prevUnclassified);
        setSiteGroups(prevSiteGroups);
        setUncTotalCount(prev => prev + idsToAssign.length);
        toast.error('현장 배정에 실패했습니다');
      }
    }
  };

  // 현장별 사진 썸네일 드래그앤드롭 → 다른 현장으로 이동 (선택된 여러 장을 함께 이동 가능, 서로 다른 현장 출신이어도 OK)
  const handleSiteThumbDrop = async (targetSiteId: number) => {
    if (!draggingSiteThumb) return;
    const { photoId: draggedId } = draggingSiteThumb;
    setDraggingSiteThumb(null);
    setDropTargetId(null);

    const idsToMove = (selectedSitePhotoIds.has(draggedId) && selectedSitePhotoIds.size > 0)
      ? Array.from(selectedSitePhotoIds)
      : [draggedId];

    const prevSiteGroups = siteGroups;
    const poolSite = allSites.find(s => s.siteId === targetSiteId);

    const sourceMap = new Map<number, number>();
    prevSiteGroups.forEach(g => g.thumbPhotos.forEach(p => { if (idsToMove.includes(p.photoId)) sourceMap.set(p.photoId, g.siteId); }));
    const actualMoves = idsToMove.filter(id => sourceMap.get(id) !== undefined && sourceMap.get(id) !== targetSiteId);
    setSelectedSitePhotoIds(new Set());
    if (!actualMoves.length) return;

    const movedPhotos: SitePhotoItem[] = [];
    prevSiteGroups.forEach(g => g.thumbPhotos.forEach(p => { if (actualMoves.includes(p.photoId)) movedPhotos.push(p); }));

    setSiteGroups(prev => {
      const withoutSource = pruneEmptyPromoted(prev.map(g => {
        const removeIds = actualMoves.filter(id => sourceMap.get(id) === g.siteId);
        if (!removeIds.length) return g;
        return {
          ...g,
          thumbPhotos: g.thumbPhotos.filter(p => !removeIds.includes(p.photoId)),
          newCount: Math.max(0, g.newCount - removeIds.length),
          totalOnSite: Math.max(0, g.totalOnSite - removeIds.length),
          sessionPhotoIds: g.sessionPhotoIds.filter(id => !removeIds.includes(id)),
        };
      }));
      if (!withoutSource.some(g => g.siteId === targetSiteId)) {
        markJustPromoted(targetSiteId);
        return [{
          siteId: targetSiteId,
          siteName: poolSite?.siteName ?? `현장 #${targetSiteId}`,
          address: poolSite?.address ?? null,
          customerName: poolSite?.customerName ?? null,
          siteStatus: poolSite?.siteStatus ?? null,
          siteStatusName: poolSite?.siteStatusName ?? null,
          chargeMembers: poolSite?.chargeMembers ?? [],
          managerMembers: poolSite?.managerMembers ?? [],
          newCount: movedPhotos.length,
          prevCount: poolSite?.photoCount ?? 0,
          thumbPhotos: movedPhotos,
          totalOnSite: (poolSite?.photoCount ?? 0) + movedPhotos.length,
          sessionPhotoIds: actualMoves,
          fromPool: true,
        }, ...withoutSource].sort(sortByName);
      }
      return withoutSource.map(g => g.siteId === targetSiteId
        ? {
            ...g,
            thumbPhotos: [...movedPhotos, ...g.thumbPhotos],
            newCount: g.newCount + movedPhotos.length,
            totalOnSite: g.totalOnSite + movedPhotos.length,
            sessionPhotoIds: [...actualMoves, ...g.sessionPhotoIds],
          }
        : g);
    });

    const user = useAuthStore.getState().user;
    if (user) {
      try {
        await photoApi.assignSite(actualMoves.map(photoId => ({ photoId, siteId: targetSiteId })));
      } catch {
        setSiteGroups(prevSiteGroups);
        toast.error('현장 이동에 실패했습니다');
      }
    }
  };

  return (
    <>
      {/* D-07 §21.4 fixed — StepBar 를 고정하고 PageScroll 이 스크롤·폭·패딩을 갖는다 (형제 화면 UploadMain·ShareMain 과 동형) */}
      <Page layout="fixed">
      <StepBar uploadCount={classifyResult?.totalCount ?? 0} fromUpload={fromUpload} />

      <PageScroll width="lg">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-md)' }}>
        {showInfoBanner && (
          <Alert kind="info" style={{ flexShrink: 0 }} onClose={() => setShowInfoBanner(false)}>
            <span>
              사진을 담을 현장이 없다면 <b>[현장 등록]</b>으로 새로 추가하거나 <b>[전체 현장 불러오기]</b>로 찾아서, 사진을 드래그해 현장 카드에 놓아보세요
            </span>
            {/* 자동 배정 후보는 올린 사람의 담당 · 관리 현장(사장님은 사업장 전체) — 서버(sv-customer gps-match/bulk) 판정 */}
            <span>- 위치정보가 있는 사진은 내가 담당 · 관리하는 현장(사장님은 전체 현장) 중 가까운 현장으로 자동 배정됩니다.</span>
          </Alert>
        )}

        <div style={{ display: 'flex', flexDirection: 'row', gap: 14, alignItems: 'flex-start' }}>

          {/* ── 좌: 미분류 패널 (sticky) ── */}
          <div style={{ flex: '0 0 340px', position: 'sticky', top: 0 }}>
            <div style={{ background: 'var(--shell-surface)', border: '1.5px solid var(--shell-border)', borderRadius: 'var(--radius-xl)', overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 160px)' }}>
              <div style={{ background: 'var(--shell-bg)', padding: '9px 16px', borderBottom: '1px solid var(--shell-border)', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, minHeight: 108, boxSizing: 'border-box' }}>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-md)', background: 'color-mix(in srgb, var(--color-error) 10%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-error)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <span style={{ fontSize: 'var(--type-body-md-size)', fontWeight: 700, color: 'var(--shell-on-surface)' }}>미분류 사진</span>
                    <span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--color-error)', background: 'color-mix(in srgb, var(--color-error) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--color-error) 25%, transparent)', borderRadius: 'var(--radius-sm)', padding: '2px 8px' }}>{uncTotalCount}장</span>
                    {selectedPhotoIds.size > 0 && (
                      <span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--gt-primary)', background: 'var(--gt-subtle)', border: '1px solid var(--gt-primary)', borderRadius: 'var(--radius-sm)', padding: '2px 8px' }}>
                        {selectedPhotoIds.size}장 선택됨
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)', marginTop: 3, display: 'block' }}>
                    {selectedPhotoIds.size > 0 ? '선택한 사진을 드래그해서 한번에 배정하세요' : '클릭으로 선택 후 드래그해서 현장에 배정하세요'}
                  </span>
                </div>
                </div>
                {hasServiceError && (
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={rematching}
                    onClick={handleRematch}
                    title="매칭 서비스 오류로 미분류된 사진을 다시 매칭합니다"
                    className="shrink-0 self-center"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                    </svg>
                    매칭 재시도
                  </Button>
                )}
              </div>

              <div ref={uncScrollRef} style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
              {loading ? (
                <div style={{ padding: '40px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 'var(--radius-lg)', background: 'var(--shell-skeleton)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="2" strokeLinecap="round" style={{ animation: 'spin 1s linear infinite' }}>
                      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                  </div>
                  <div style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)' }}>불러오는 중...</div>
                </div>
              ) : unclassifiedItems.length === 0 ? (
                <div style={{ padding: '40px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 'var(--radius-lg)', background: 'color-mix(in srgb, var(--color-success) 8%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                  </div>
                  <div style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)' }}>미분류 사진이 없습니다</div>
                  <div style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)', textAlign: 'center', lineHeight: 1.5 }}>모든 사진이 현장에 배정되었습니다.</div>
                </div>
              ) : (
                <>
                  {newUnclassified.length > 0 && (
                    <div style={{ padding: '14px 20px 6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                        <span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--gt-primary)', textTransform: 'uppercase' }}>이번 업로드</span>
                        <span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--gt-primary)', background: 'var(--gt-subtle)', borderRadius: 'var(--radius-md)', padding: '1px 8px' }}>{newUploadTotalCount}장</span>
                        <div style={{ flex: 1, height: 1, background: 'var(--shell-border)' }} />
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }} onMouseDown={handleContainerMouseDown}>
                        {newUnclassified.map((p, pi) => (
                          <UncPhotoCard key={p.photoId} item={p}
                            dragging={isDragging(p.photoId)}
                            isSelected={selectedPhotoIds.has(p.photoId)}
                            selectedCount={selectedPhotoIds.size}
                            onDragStart={() => setDraggingPhotoId(p.photoId)}
                            onDragEnd={() => { setDraggingPhotoId(null); setDropTargetId(null); }}
                            onRemove={() => handleRemoveUnclassified(p.photoId)}
                            onSelect={(e) => handleCardClick(p.photoId, e)}
                            onViewPhoto={() => {
                              const all = [...newUnclassified, ...oldUnclassified];
                              setViewer({
                                photos: all.map(u => ({ photoId: u.photoId, thumbUrl: u.thumbUrl, originalUrl: null as string | null, takenAt: u.takenDtm, tags: [] as { tagType: string; tagValue: string }[] })),
                                initialIndex: pi,
                              });
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {newUnclassified.length > 0 && oldUnclassified.length > 0 && (
                    <div style={{ margin: '10px 20px', borderTop: '1.5px dashed var(--shell-border)' }} />
                  )}

                  {oldUnclassified.length > 0 && (
                    <div style={{ padding: `${newUnclassified.length > 0 ? '6px' : '14px'} 20px 16px` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                        <span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--shell-hint)', textTransform: 'uppercase' }}>이전 미분류</span>
                        <span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--shell-hint)', background: 'var(--shell-skeleton)', borderRadius: 'var(--radius-md)', padding: '1px 8px' }}>{oldUnclassifiedTotalCount}장</span>
                        <div style={{ flex: 1, height: 1, background: 'var(--shell-border)' }} />
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }} onMouseDown={handleContainerMouseDown}>
                        {oldUnclassified.map(p => (
                          <UncPhotoCard key={p.photoId} item={p}
                            dragging={isDragging(p.photoId)}
                            isSelected={selectedPhotoIds.has(p.photoId)}
                            selectedCount={selectedPhotoIds.size}
                            onDragStart={() => setDraggingPhotoId(p.photoId)}
                            onDragEnd={() => { setDraggingPhotoId(null); setDropTargetId(null); }}
                            onRemove={() => handleRemoveUnclassified(p.photoId)}
                            onSelect={(e) => handleCardClick(p.photoId, e)}
                            onViewPhoto={() => {
                              const all = [...newUnclassified, ...oldUnclassified];
                              const idx = newUnclassified.length + oldUnclassified.findIndex(u => u.photoId === p.photoId);
                              setViewer({
                                photos: all.map(u => ({ photoId: u.photoId, thumbUrl: u.thumbUrl, originalUrl: null as string | null, takenAt: u.takenDtm, tags: [] as { tagType: string; tagValue: string }[] })),
                                initialIndex: Math.max(0, idx),
                              });
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
              {uncHasMore && (
                <div ref={uncSentinelRef} style={{ padding: '12px 0 8px', textAlign: 'center' }}>
                  {uncLoadingMore && <span style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)' }}>불러오는 중...</span>}
                </div>
              )}
              </div>
            </div>
          </div>

          {/* ── 우: 현장 리스트 패널 ── */}
          <div style={{ flex: 1 }}>
            <div style={{ background: 'var(--shell-surface)', borderRadius: 'var(--radius-xl)', overflow: 'hidden', boxShadow: 'var(--elevation-2)', border: '1.5px solid var(--shell-border)' }}>
              <div style={{ background: 'linear-gradient(135deg, var(--gt-subtle) 0%, var(--shell-surface) 100%)', padding: '9px 16px 12px', borderBottom: '1px solid var(--shell-border)', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 10, minHeight: 108, boxSizing: 'border-box' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-md)', background: 'var(--gt-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <span style={{ fontSize: 'var(--type-body-md-size)', fontWeight: 700, color: 'var(--shell-on-surface)' }}>현장별 사진</span>
                      <span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--gt-primary)', background: 'var(--gt-subtle)', border: '1px solid var(--gt-primary)', borderRadius: 'var(--radius-sm)', padding: '2px 8px' }}>{siteGroups.length}곳</span>
                    </div>
                  </div>
                  {/* 전체 현장 불러오기 토글 + 현장 등록 — 타이틀 우측 정렬 */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <Button
                      variant={allSitesVisible ? 'primary' : 'secondary'}
                      size="sm"
                      onClick={handleToggleAllSites}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>
                      {allSitesVisible ? '현장 숨기기' : allSitesStarted ? '현장 전체보기' : '전체 현장 불러오기'}
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => setShowSiteRegPopup(true)}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                      현장 등록
                    </Button>
                  </div>
                </div>

                {/* 검색필터(항상 고정 노출) */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Search
                      value={allSitesKeyword}
                      onChange={handleAllSitesKeywordChange}
                      onClear={() => handleAllSitesKeywordChange('')}
                      placeholder="주소·건물명 검색"
                      maxLength={50}
                    />
                  </div>
                  {/* 담당자·관리자 조건 — 주소·건물명 검색창 오른쪽, 라벨+선택 같은 줄(D-5). 전체 현장 목록에만 적용 */}
                  <AssigneeFilterField
                    members={companyMembers}
                    values={allSitesMemberIds}
                    onChange={handleAllSitesMembersChange}
                    myId={assignMyId}
                  />
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, flexShrink: 0 }}>
                    {ALL_SITES_STATUS_FILTERS.map(({ label, filter }) => (
                      <FilterChip
                        key={filter}
                        active={allSitesStatusFilter === filter}
                        onClick={() => handleAllSitesStatusChange(filter)}
                        size="md"
                      >
                        {label}
                      </FilterChip>
                    ))}
                  </div>
                </div>
              </div>

              <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 9 }}>
                {loading ? (
                  <div style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)', textAlign: 'center', padding: '24px 0' }}>불러오는 중...</div>
                ) : siteGroups.length === 0 && allSitesAsGroups.length === 0 && allSitesFilteredEmpty ? (
                  // 전체 현장을 불러왔는데 담당자·관리자 등 조회 조건 때문에 0건 — 불러오기 유도 대신 조건 안내
                  <div style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)', textAlign: 'center', padding: 'var(--spacing-09) var(--spacing-05)' }}>
                    조건에 맞는 현장이 없습니다.
                  </div>
                ) : siteGroups.length === 0 && allSitesAsGroups.length === 0 ? (
                  <div style={{ padding: '40px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 'var(--radius-lg)', background: 'var(--shell-skeleton)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                    </div>
                    <div style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)' }}>분류된 현장이 없습니다</div>
                    <div style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)', textAlign: 'center', lineHeight: 1.5 }}>전체 현장을 불러와서 사진을 배정해보세요.</div>
                    <Button variant="secondary" size="sm" onClick={handleToggleAllSites} style={{ marginTop: 4 }}>
                      현장 전체보기
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* 썸네일 영역이 카드 자체 폭(container query)에 따라 4줄→3줄→2줄로 줄어들도록. minWidth를 낮춰 열 수도 함께 줄어든다 */}
                    <style>{`
                      .pom-site-thumbs { max-height: 308px; }
                      @container pom-site-card (max-width: 560px) { .pom-site-thumbs { max-height: 216px; } }
                      @container pom-site-card (max-width: 420px) { .pom-site-thumbs { max-height: 144px; } }
                    `}</style>
                    {[...siteGroups, ...allSitesAsGroups].map(group => {
                      const isDrop = dropTargetId === group.siteId;

                      return (
                        <div
                          key={group.siteId}
                          onDragOver={e => { e.preventDefault(); setDropTargetId(group.siteId); }}
                          onDragLeave={() => setDropTargetId(null)}
                          onDrop={() => { draggingSiteThumb ? void handleSiteThumbDrop(group.siteId) : void handleDrop(group.siteId); }}
                          onMouseDown={handleSiteContainerMouseDown}
                          style={{
                            background: isDrop ? 'var(--gt-subtle)' : 'var(--shell-surface)',
                            border: isDrop ? 'none' : '1.5px solid var(--shell-border)',
                            outline: isDrop ? '2.5px dashed var(--gt-primary)' : 'none',
                            boxShadow: isDrop ? '0 0 0 3px var(--gt-subtle)' : 'none',
                            borderRadius: 'var(--radius-lg)', padding: '12px 16px',
                            cursor: 'pointer', transition: 'all .18s',
                            display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 14, minHeight: 96,
                            boxSizing: 'border-box',
                            containerType: 'inline-size', containerName: 'pom-site-card',
                          }}
                        >
                          {/* 현장 정보 — 내용만큼만 차지하고, 남는 가로 공간은 썸네일 쪽으로 넘김 */}
                          <div style={{ flex: '0 1 auto', minWidth: 0, maxWidth: 320, display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                              {group.siteStatus && (
                                <Badge status={siteStatusBadge(group.siteStatus)} className="shrink-0">
                                  {group.siteStatusName ?? group.siteStatus}
                                </Badge>
                              )}
                              <span style={{ fontSize: 'var(--type-body-md-size)', fontWeight: 700, color: 'var(--shell-on-surface)', lineHeight: 1.3 }}>
                                {group.siteName}
                              </span>
                              {group.newCount > 0 && (
                                <span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--gt-on-primary)', background: 'var(--gt-primary)', borderRadius: 'var(--radius-sm)', padding: '2px 7px', flexShrink: 0 }}>
                                  NEW {group.newCount}장
                                </span>
                              )}
                              {group.siteId === justPromotedSiteId && (
                                <span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--color-success)', background: 'color-mix(in srgb, var(--color-success) 12%, transparent)', borderRadius: 'var(--radius-sm)', padding: '2px 7px', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 3 }}>
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
                                  방금 배정됨
                                </span>
                              )}
                            </div>
                            {group.customerName && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)' }}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                                {group.customerName}
                              </div>
                            )}
                            {group.address && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)' }}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                                {group.address}
                              </div>
                            )}
                            {/* 담당자 · 관리자 배지 줄 — 카드 주소 아래(D-5) */}
                            <AssigneeBadges
                              className="mb-02 mt-03"
                              chargeMembers={group.chargeMembers}
                              managerMembers={group.managerMembers}
                              filterIds={allSitesMemberIds}
                              myId={assignMyId}
                            />
                            <div style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-on-surface-2)', marginTop: 1 }}>
                              이전 분석 사진 <span style={{ fontWeight: 700, color: 'var(--gt-primary)' }}>{group.prevCount}장</span>
                            </div>
                          </div>

                          {/* 썸네일 + [-] 버튼 — 4장 초과분도 클릭 없이 항상 전부 표시(드래그 가능). 카드 폭에 따라 4줄→3줄→2줄로 줄어들고(container query), 그 이상은 내부 스크롤 */}
                          <div
                            className="pom-site-thumbs"
                            style={{
                              display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
                              flex: '1 1 300px', minWidth: 160, overflowY: 'auto', overflowX: 'hidden', alignContent: 'flex-start',
                              padding: '8px 12px 0 8px',
                            }}
                          >
                            {group.thumbPhotos.map((photo, i) => (
                              <SiteThumb
                                key={photo.photoId}
                                photo={photo}
                                onClick={() => {
                                  if (!group.thumbPhotos.length) return;
                                  setViewer({
                                    photos: group.thumbPhotos.map(p => ({ photoId: p.photoId, thumbUrl: p.thumbUrl, originalUrl: null as string | null, takenAt: p.takenAt, tags: [] as { tagType: string; tagValue: string }[] })),
                                    initialIndex: Math.min(i, group.thumbPhotos.length - 1),
                                  });
                                }}
                                onMoveToUnclassified={() => void handleMoveToUnclassified(group.siteId, photo)}
                                dragging={isSiteThumbDragging(photo.photoId)}
                                isSelected={selectedSitePhotoIds.has(photo.photoId)}
                                selectedCount={selectedSitePhotoIds.size}
                                onDragStart={() => setDraggingSiteThumb({ photoId: photo.photoId, siteId: group.siteId })}
                                onDragEnd={() => { setDraggingSiteThumb(null); setDropTargetId(null); }}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })}

                    {allSitesVisible && allSitesHasMore && (
                      <div ref={allSitesSentinelRef} style={{ padding: '8px 0', textAlign: 'center' }}>
                        {allSitesLoading && <span style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)' }}>불러오는 중...</span>}
                      </div>
                    )}
                    {allSitesVisible && allSitesStarted && !allSitesLoading && visibleAllSites.length === 0 && (
                      <div style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)', textAlign: 'center', padding: '16px 0' }}>
                        {allSitesKeyword ? '검색 결과가 없어요' : allSitesFilteredEmpty ? '조건에 맞는 현장이 없습니다.' : '더 불러올 현장이 없어요'}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        </div>
      </PageScroll>
      </Page>

      {/* 삭제 확인 모달 */}
      <Modal open={deleteConfirmId !== null} onClose={() => setDeleteConfirmId(null)}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '10px 4px' }}>
          <div style={{ width: 52, height: 52, borderRadius: 'var(--radius-xl)', background: 'color-mix(in srgb, var(--color-error) 8%, transparent)', border: '1.5px solid color-mix(in srgb, var(--color-error) 25%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-error)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
          </div>
          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: 'var(--type-heading-sm-size)', fontWeight: 700, color: 'var(--shell-on-surface)' }}>사진을 삭제하시겠습니까?</div>
            <div style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)', lineHeight: 1.6 }}>삭제된 사진은 복구할 수 없습니다.</div>
          </div>
          <div style={{ display: 'flex', gap: 8, width: '100%', marginTop: 4 }}>
            <Button variant="primary" className="flex-1" onClick={() => setDeleteConfirmId(null)}>취소</Button>
            <Button variant="etc" className="flex-1" onClick={confirmDelete}>삭제</Button>
          </div>
        </div>
      </Modal>

      {showSiteRegPopup && (
        <SiteRegistrationPopup
          agentId={useAppStore.getState().subscriptions.find(s => s.agentType === 'A01')?.agentId ?? 1}
          onClose={() => setShowSiteRegPopup(false)}
          onComplete={(siteId: number) => void handleSiteRegComplete(siteId)}
        />
      )}

      {viewer && (
        <PhotoViewerLayer
          photos={viewer.photos}
          initialIndex={viewer.initialIndex}
          onClose={() => setViewer(null)}
        />
      )}

      {/* 하단 버튼 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderTop: '1px solid var(--shell-border)', background: 'var(--shell-surface)', flexShrink: 0 }}>
        <Button variant="secondary" size="lg" onClick={() => navigate(`${BASE}/upload`)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          이전
        </Button>
        <Button
          variant="primary"
          size="lg"
          style={{ boxShadow: '0 4px 16px color-mix(in srgb, var(--gt-primary) 35%, transparent)' }}
          onClick={() => {
            setClassifySiteGroups(siteGroups);
            setTagEditGroups(siteGroups
              .filter(g => g.sessionPhotoIds.length > 0)
              .map(g => ({
                siteId: g.siteId,
                siteName: g.siteName,
                totalOnSite: g.totalOnSite,
                thumbPhotos: g.thumbPhotos,
                sessionPhotoIds: g.sessionPhotoIds,
              })));
            // 현장상세 → 태그 등록 진입 시 남았을 수 있는 복귀 경로를 지워, 업로드 플로우의 정상 StepBar/이전 버튼이 보이게 한다
            setTagEditReturnPath(null);
            navigate(`${BASE}/tag-bulk-edit`);
          }}
        >
          다음
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
        </Button>
      </div>
    </>
  );
}
